import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import {
  convertKeysToCamelCase,
  ResponseError,
} from '@ltvco/refresh-lib/utils';
import { useServiceToken } from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useMemo } from 'react';

import {
  buildLeadScoreCopyRequest,
  createLeadScoreCopy,
  type BuildLeadScoreCopyRequestParams,
  type LeadScoreCopyData,
} from 'api/leadScoreCopy';

import type { LeadScoreReason, LeadScoreResultData } from 'api/leadScore';

interface TokenData {
  token: string;
}

const devTokenOverride = import.meta.env.DEV
  ? import.meta.env.VITE_LLM_DEV_TOKEN
  : undefined;

interface UseLeadScoreCopyProps {
  input: BuildLeadScoreCopyRequestParams | null;
  enabled?: boolean;
}

interface UseLeadScoreCopyReturn {
  copy: LeadScoreCopyData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export const useLeadScoreCopy = ({
  input,
  enabled = true,
}: UseLeadScoreCopyProps): UseLeadScoreCopyReturn => {
  const { logError } = useAppConfigContext();
  const { data: tokenData, isLoading: isTokenLoading } = useServiceToken(
    'llm'
  ) as {
    data: TokenData | undefined;
    isLoading: boolean;
  };

  const requestParams = useMemo(
    () => (input ? buildLeadScoreCopyRequest(input) : null),
    [input]
  );

  const token = devTokenOverride || tokenData?.token;
  const queryEnabled =
    Boolean(enabled) && Boolean(requestParams) && Boolean(token);

  const query = useQuery({
    queryKey: ['leadScoreCopy', requestParams],
    queryFn: async () => {
      if (!token) {
        throw new Error('No authentication token available');
      }
      if (!requestParams) {
        throw new Error('No lead score copy request params available');
      }

      const response = await createLeadScoreCopy(token, requestParams);

      return convertKeysToCamelCase(response) as LeadScoreCopyData;
    },
    enabled: queryEnabled,
    onError: (error: ResponseError) => {
      if (error.response?.status === 429) {
        logError('lead-score-copy: rate limit error', error);
      } else {
        logError('lead-score-copy: generation failed', error);
      }
    },
  });

  return {
    copy: query.data,
    isLoading: isTokenLoading || (queryEnabled && query.isLoading),
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

export interface MergedLeadScoreCopy {
  headline: string;
  description: string;
  reasons: LeadScoreReason[];
  isGenerating: boolean;
  isError: boolean;
}

const toCopyRequestInput = (
  result: LeadScoreResultData
): BuildLeadScoreCopyRequestParams => ({
  configId: result.config,
  useCase: result.useCase,
  address: result.address,
  score: result.score,
  maxScore: result.maxScore,
  tier: result.tier,
  action: result.action,
  signals: result.signals,
  contactability: result.contactability,
});

export const useLeadScoreCopyForResult = (
  result: LeadScoreResultData | undefined
): MergedLeadScoreCopy | undefined => {
  const input = useMemo(
    () => (result ? toCopyRequestInput(result) : null),
    [result]
  );

  const { copy, isLoading, isError } = useLeadScoreCopy({
    input,
    enabled: Boolean(result),
  });

  const reasons = useMemo(() => {
    if (!result) {
      return [];
    }
    return copy?.reasons?.length
      ? copy.reasons.map((reason) => ({ segments: reason.segments }))
      : result.reasons;
  }, [copy, result]);

  if (!result) {
    return undefined;
  }

  return {
    // While the AI copy request is in flight, render nothing rather than
    // jumping straight to the deterministic fallback — that fallback is
    // reserved for when the AI response never comes back (error or empty).
    headline: isLoading ? '' : copy?.headline ?? result.headline,
    description: isLoading ? '' : copy?.description ?? result.description,
    reasons,
    isGenerating: isLoading,
    isError,
  };
};
